import { useMemo } from 'react';
import { Alert, Card, Checkbox, Collapse, Empty, Space, Tag, Typography } from 'antd';
import type { AdminRole } from '../auth/store';
import { useRbacCatalog, type ModuleDef, type PageDef } from './catalog';

/**
 * The permission checkbox tree, generated from the API's catalogue — so it is
 * always exactly the set of keys the API guard enforces, and a permission added
 * server-side appears here with no frontend release.
 *
 * Two kinds of tick appear:
 *
 *   • GRANT — a box the operator controls. Stored in `admin_permissions`.
 *   • FROM ROLE — already carried by the admin's built-in role template. Shown
 *     checked and locked, with a tag, because a grant can only widen a role,
 *     never narrow it. Picking `sub_admin` (an empty template) is what makes
 *     every box a free choice.
 *
 * `value` is the GRANT list only; role-template keys are never written into it,
 * so switching an operator's role never leaves stale grants behind.
 */
export function PermissionEditor({
  role,
  value,
  onChange,
  disabled = false,
}: {
  role: AdminRole;
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const catalog = useRbacCatalog();
  const fromRole = useMemo(
    () => new Set(catalog.data?.roleTemplates?.[role] ?? []),
    [catalog.data, role],
  );
  const granted = useMemo(() => new Set(value), [value]);
  const grantOnly = catalog.data?.grantOnlyRoles?.includes(role) ?? false;

  if (catalog.isLoading) return <Card loading />;
  if (!catalog.data) {
    return <Alert type="error" showIcon message="Could not load the permission catalogue" />;
  }

  /** Keys in a page/module that the operator can actually toggle. */
  const togglable = (keys: string[]) => keys.filter((k) => !fromRole.has(k));

  const setMany = (keys: string[], on: boolean) => {
    const next = new Set(granted);
    for (const k of togglable(keys)) {
      if (on) next.add(k);
      else next.delete(k);
    }
    onChange([...next].sort());
  };

  const pageKeys = (p: PageDef) => p.permissions.map((x) => x.key);
  const moduleKeys = (m: ModuleDef) => m.pages.flatMap(pageKeys);

  const countOf = (keys: string[]) =>
    keys.filter((k) => granted.has(k) || fromRole.has(k)).length;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {!grantOnly && (
        <Alert
          type="info"
          showIcon
          message={`The ${role} role already carries ${fromRole.size} permissions`}
          description="Those are shown ticked and locked — a grant can widen a role but never narrow it. To choose every permission by hand, set the role to sub_admin."
        />
      )}
      <Collapse
        accordion={false}
        defaultActiveKey={catalog.data.modules.map((m) => m.key)}
        items={catalog.data.modules.map((m) => {
          const keys = moduleKeys(m);
          const selectable = togglable(keys);
          const on = countOf(keys);
          return {
            key: m.key,
            label: (
              <Space>
                <Typography.Text strong>{m.label}</Typography.Text>
                <Tag color={on > 0 ? 'blue' : 'default'}>
                  {on}/{keys.length}
                </Tag>
              </Space>
            ),
            extra: (
              <Checkbox
                disabled={disabled || selectable.length === 0}
                checked={selectable.length > 0 && selectable.every((k) => granted.has(k))}
                indeterminate={
                  selectable.some((k) => granted.has(k)) &&
                  !selectable.every((k) => granted.has(k))
                }
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => setMany(keys, e.target.checked)}
              >
                All
              </Checkbox>
            ),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {m.pages.map((p) => (
                  <div key={p.label}>
                    <Space size={8} style={{ marginBottom: 6 }}>
                      <Typography.Text strong style={{ fontSize: 13 }}>
                        {p.label}
                      </Typography.Text>
                      {p.route && (
                        <Typography.Text code style={{ fontSize: 11 }}>
                          {p.route}
                        </Typography.Text>
                      )}
                    </Space>
                    <div style={{ display: 'grid', gap: 6, paddingLeft: 4 }}>
                      {p.permissions.map((perm) => {
                        const locked = fromRole.has(perm.key);
                        return (
                          <Checkbox
                            key={perm.key}
                            disabled={disabled || locked}
                            checked={locked || granted.has(perm.key)}
                            onChange={(e) => setMany([perm.key], e.target.checked)}
                          >
                            <Space size={6} wrap>
                              <span>{perm.label}</span>
                              <Typography.Text code style={{ fontSize: 11 }}>
                                {perm.key}
                              </Typography.Text>
                              {perm.kind === 'view' && <Tag color="geekblue">page</Tag>}
                              {locked && <Tag color="default">from {role}</Tag>}
                              {perm.hint && (
                                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                                  {perm.hint}
                                </Typography.Text>
                              )}
                            </Space>
                          </Checkbox>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </Space>
            ),
          };
        })}
      />
      {catalog.data.modules.length === 0 && <Empty description="No permissions in the catalogue" />}
    </Space>
  );
}
